// ─── resolveFuelPrice — single source of truth for €/L per country ────
//
// Hierarquia (do mais específico ao mais genérico):
//   1. Override inline no card "Combustível por País"        (per-route, per-country)
//   2. Preferências do cliente (fuelPricesByCountry)         (per-tenant, per-country)
//   3. route.fuelPrice da Sidebar                            (per-route, só se 1 país atravessado)
//   4. EU Oil Bulletin (fuelPrices Redux)                    (automático, só países UE)
//   5. fuelDef.priceDefault                                  (fallback europeu)
//
// Retorna SEMPRE em EUR — quem consome multiplica por currency.rate
// para mostrar na moeda do tenant. Os preços nas preferências do cliente
// estão guardados na **moeda local de cada país** (Kz para AGO, EUR para
// PRT/ESP), por isso este helper precisa do mapa countryCode → rate.

import { CURRENCIES } from './currencies';

// ISO-3 → ISO-2 (FuelCountryPanel usa ISO-3 a vir do HERE, mas o EU Oil
// Bulletin e as Preferências do cliente são ISO-2 para minimizar erros
// de digitação. Este mapa cobre os países onde de facto operamos hoje.)
const ISO3_TO_ISO2 = {
  PRT: 'PT', ESP: 'ES', FRA: 'FR', DEU: 'DE', ITA: 'IT',
  BEL: 'BE', NLD: 'NL', LUX: 'LU', AUT: 'AT', CHE: 'CH',
  GBR: 'GB', IRL: 'IE', DNK: 'DK', SWE: 'SE', NOR: 'NO',
  FIN: 'FI', POL: 'PL', CZE: 'CZ', HUN: 'HU', SVK: 'SK',
  SVN: 'SI', HRV: 'HR', ROU: 'RO', BGR: 'BG', GRC: 'GR',
  AGO: 'AO', NAM: 'NA', ZAF: 'ZA', MOZ: 'MZ', CPV: 'CV',
};

// Moeda local típica de cada país. Usamos isto para converter o preço
// das Preferências (em moeda local) para EUR. Ex.: AO usa AOA, PT usa
// EUR. Para países sem entrada cai em EUR (preço já está em EUR).
const COUNTRY_CURRENCY = {
  // Europa Ocidental + Norte
  PT: 'EUR', ES: 'EUR', FR: 'EUR', DE: 'EUR', IT: 'EUR', BE: 'EUR',
  NL: 'EUR', LU: 'EUR', AT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
  CH: 'CHF', GB: 'GBP', DK: 'DKK', SE: 'SEK', NO: 'NOK', IS: 'ISK',
  // Europa Central & Leste
  PL: 'PLN', CZ: 'CZK', SK: 'EUR', HU: 'HUF', RO: 'RON', BG: 'BGN',
  HR: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR', MT: 'EUR',
  CY: 'EUR', RS: 'RSD',
  // África Lusófona
  AO: 'AOA', MZ: 'MZN', CV: 'CVE', ST: 'STN', GW: 'XOF', GQ: 'XAF',
  // África Austral
  ZA: 'ZAR', NA: 'NAD', BW: 'BWP', ZM: 'ZMW', ZW: 'ZWL', MW: 'MWK',
  MU: 'MUR', MG: 'MGA',
  // África Central
  CM: 'XAF', GA: 'XAF', CG: 'XAF', CD: 'CDF', RW: 'RWF',
  // África Ocidental
  NG: 'NGN', GH: 'GHS', CI: 'XOF', SN: 'XOF', ML: 'XOF', BF: 'XOF',
  // África Oriental
  KE: 'KES', TZ: 'TZS', UG: 'UGX', ET: 'ETB',
  // Norte de África
  MA: 'MAD', DZ: 'DZD', TN: 'TND', EG: 'EGP', LY: 'LYD',
};

function toIso2(cc) {
  if (!cc) return null;
  const u = String(cc).toUpperCase();
  if (u.length === 2) return u;
  return ISO3_TO_ISO2[u] || u.slice(0, 2);
}

function localToEUR(priceLocal, currencyCode) {
  if (priceLocal == null || !isFinite(priceLocal)) return null;
  if (!currencyCode || currencyCode === 'EUR') return priceLocal;
  const cur = CURRENCIES.find(c => c.code === currencyCode);
  if (!cur || !cur.rate) return priceLocal; // sem rate → assume EUR
  return priceLocal / cur.rate;
}

/**
 * @param {string} cc                   ISO-2 ou ISO-3 do país do span
 * @param {string} fuelType             'diesel' | 'gasoline' | …
 * @param {Object} params
 * @param {Object} params.overrides     fuelPriceOverrides do Redux (per-route inline)
 * @param {Object} params.clientSettings clientSettings do AuthContext (com fuelPricesByCountry)
 * @param {Object} params.route          rota actual (lê route.fuelPrice)
 * @param {Object} params.fuelPrices     EU Oil Bulletin slice
 * @param {number} params.routeRate      currency.rate da rota (para converter route.fuelPrice → EUR)
 * @param {Object} params.fuelDef        FUEL_TYPES entry (para priceDefault)
 * @param {boolean} params.singleCountry true se a rota só tem 1 país
 * @returns {number} preço em EUR/litro
 */
export function resolveFuelPriceEUR(cc, fuelType, {
  overrides = {},
  clientSettings = {},
  route = {},
  fuelPrices = {},
  routeRate = 1,
  fuelDef = { priceDefault: 1.65 },
  singleCountry = false,
}) {
  const iso2 = toIso2(cc);

  // 1. Override inline no card (já guardado em EUR pelo slice)
  if (overrides && overrides[cc] != null) return overrides[cc];
  if (overrides && iso2 && overrides[iso2] != null) return overrides[iso2];

  // 2. Preferências do cliente — preço em moeda local do país
  const prefsMap = clientSettings.fuelPricesByCountry || {};
  const prefsEntry = (iso2 && prefsMap[iso2]) || null;
  if (prefsEntry && prefsEntry[fuelType] != null) {
    const localCurrency = COUNTRY_CURRENCY[iso2] || 'EUR';
    const inEUR = localToEUR(Number(prefsEntry[fuelType]), localCurrency);
    if (inEUR != null && isFinite(inEUR) && inEUR > 0) return inEUR;
  }

  // 3. Sidebar route.fuelPrice — só faz sentido se a rota é mono-país.
  //    Em rotas multi-país (UE típicas) deixamos a EU Oil Bulletin
  //    decidir por país.
  if (singleCountry && route.fuelPrice != null && routeRate > 0) {
    return Number(route.fuelPrice) / routeRate;
  }

  // 4. EU Oil Bulletin
  const apiEntry = fuelPrices[cc] || (iso2 && fuelPrices[iso2]);
  if (apiEntry && apiEntry[fuelType]) return apiEntry[fuelType].price;

  // 5. Fallback europeu
  return fuelDef.priceDefault;
}
